// One survey section: scorecard items on the 1-4 scale, then the written
// reflection questions. Extracted from SurveyPage so the admin instrument
// editor's live preview renders respondents' EXACT component. Instrument text
// renders through the inline-markup tokenizer; follow-up questions sit
// indented under their parent (a scale item or a reflection question).

import type { JSX, Ref } from 'react';
import { DictationButton } from '@cca/ui';
import type { InstrumentItem, InstrumentSection } from '../api';
import { partitionFollowUps, renderInstrumentText } from '../markup';
import { SegmentedScale } from './SegmentedScale';

export type ScoreMap = Record<string, number | null>;

const TEXT_MAX = 5000;

// Append a dictated segment to the current answer (the respondent's own words,
// verbatim). Dictation only — the survey never takes AI blob-fill, so a person's
// reflection is always theirs.
function appendDictation(existing: string, segment: string): string {
  const joined = existing ? `${existing} ${segment}` : segment;
  return joined.slice(0, TEXT_MAX);
}

const groupHeadingStyle = {
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--health-accent)',
  margin: '4px 0 10px',
} as const;

function FollowUpQuestion({
  question,
  texts,
  onText,
}: {
  question: InstrumentItem;
  texts: Record<string, string>;
  onText: (id: string, value: string) => void;
}): JSX.Element {
  return (
    <div
      style={{
        marginTop: 10,
        marginLeft: 18,
        paddingLeft: 14,
        borderLeft: '2px solid var(--health-border-soft, #e5e0d5)',
      }}
    >
      <label
        htmlFor={`txt-${question.id}`}
        style={{ display: 'block', fontSize: 13.5, lineHeight: 1.6, marginBottom: 6 }}
      >
        {renderInstrumentText(question.text)}
      </label>
      <textarea
        id={`txt-${question.id}`}
        className="health-textarea"
        value={texts[question.id] ?? ''}
        maxLength={TEXT_MAX}
        onChange={(e) => onText(question.id, e.target.value)}
      />
      <div style={{ marginTop: 4, textAlign: 'right' }}>
        <DictationButton
          onText={(seg) => onText(question.id, appendDictation(texts[question.id] ?? '', seg))}
        />
      </div>
    </div>
  );
}

export function SectionStep({
  section,
  scale,
  scores,
  texts,
  onScore,
  onText,
  scaleHelpOpen,
  onScaleHelpToggle,
  headingRef,
}: {
  section: InstrumentSection;
  scale: Record<string, string>;
  scores: ScoreMap;
  texts: Record<string, string>;
  onScore: (id: string, value: number | undefined) => void;
  onText: (id: string, value: string) => void;
  /** Controlled "What do the numbers mean?" state, lifted by the caller so
   *  expanding it in ONE section mirrors across ALL sections (Steve,
   *  2026-07-05). Omit both props for the old per-section behaviour. */
  scaleHelpOpen?: boolean;
  onScaleHelpToggle?: (open: boolean) => void;
  /** The survey page's step heading (SurveyPage, cca-iwefb). Given, the
   *  heading takes focus by script only (tabIndex -1) and wears the module's
   *  focus ring (.health-step-heading), so the page can move focus to it on
   *  arriving at this section. The admin previews omit it. */
  headingRef?: Ref<HTMLHeadingElement>;
}): JSX.Element {
  const { topReflection, followUps } = partitionFollowUps(section);
  let lastGroup: string | undefined;
  let lastReflectionGroup: string | undefined;
  return (
    <div className="card">
      <h2
        ref={headingRef}
        tabIndex={headingRef ? -1 : undefined}
        className={
          headingRef ? 'health-section-heading health-step-heading' : 'health-section-heading'
        }
      >
        <span>
          {section.no}. {renderInstrumentText(section.title)}
        </span>
      </h2>
      <p
        style={{ fontSize: 12.5, color: 'var(--health-grey)', margin: '0 0 16px', lineHeight: 1.6 }}
      >
        {renderInstrumentText(section.aim)}
      </p>

      <details
        style={{ marginBottom: 18, fontSize: 13 }}
        {...(onScaleHelpToggle ? { open: scaleHelpOpen === true } : {})}
      >
        <summary
          style={{ cursor: 'pointer', color: 'var(--health-accent-dk)', fontWeight: 600 }}
          onClick={
            onScaleHelpToggle
              ? (e) => {
                  // Controlled: stop the browser toggling this one <details>
                  // and flip the shared state so every section follows.
                  e.preventDefault();
                  onScaleHelpToggle(scaleHelpOpen !== true);
                }
              : undefined
          }
        >
          What do the numbers 1 to 4 mean?
        </summary>
        <div className="scale-legend">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className="scale-legend-row">
              <strong>{n}</strong>
              <span>{renderInstrumentText(scale[String(n)] ?? '')}</span>
            </div>
          ))}
        </div>
      </details>

      <div style={{ display: 'grid', gap: 20 }}>
        {section.scorecard.map((item) => {
          const groupHeading = item.group && item.group !== lastGroup ? item.group : null;
          lastGroup = item.group;
          return (
            <div key={item.id}>
              {groupHeading && (
                <h3 style={groupHeadingStyle}>{renderInstrumentText(groupHeading)}</h3>
              )}
              <p id={`lbl-${item.id}`} style={{ fontSize: 14.5, lineHeight: 1.6, marginBottom: 8 }}>
                {renderInstrumentText(item.text)}
              </p>
              {item.note && (
                <p style={{ fontSize: 12.5, color: 'var(--health-grey)', margin: '-4px 0 8px' }}>
                  {renderInstrumentText(item.note)}
                </p>
              )}
              <SegmentedScale
                itemId={item.id}
                labelId={`lbl-${item.id}`}
                value={scores[item.id] ?? undefined}
                scaleLabels={scale}
                onChange={(v) => onScore(item.id, v)}
              />
              {(followUps.get(item.id) ?? []).map((fu) => (
                <FollowUpQuestion key={fu.id} question={fu} texts={texts} onText={onText} />
              ))}
            </div>
          );
        })}
      </div>

      {topReflection.length > 0 && (
        <div style={{ marginTop: 26, borderTop: '1px solid #f0ebde', paddingTop: 18 }}>
          <h3 style={{ fontSize: 14, marginBottom: 4 }}>For reflection</h3>
          <p style={{ fontSize: 12.5, color: 'var(--health-grey)', marginBottom: 14 }}>
            These are optional. Write as much or as little as you like; your words are shared
            without your name.
          </p>
          <div style={{ display: 'grid', gap: 16 }}>
            {topReflection.map((qn) => {
              const groupHeading = qn.group && qn.group !== lastReflectionGroup ? qn.group : null;
              lastReflectionGroup = qn.group;
              return (
                <div key={qn.id}>
                  {groupHeading && (
                    <h4 style={groupHeadingStyle}>{renderInstrumentText(groupHeading)}</h4>
                  )}
                  <label
                    htmlFor={`txt-${qn.id}`}
                    style={{ display: 'block', fontSize: 14, lineHeight: 1.6, marginBottom: 6 }}
                  >
                    {renderInstrumentText(qn.text)}
                  </label>
                  <textarea
                    id={`txt-${qn.id}`}
                    className="health-textarea"
                    value={texts[qn.id] ?? ''}
                    maxLength={TEXT_MAX}
                    onChange={(e) => onText(qn.id, e.target.value)}
                  />
                  <div style={{ marginTop: 4, textAlign: 'right' }}>
                    <DictationButton
                      onText={(seg) => onText(qn.id, appendDictation(texts[qn.id] ?? '', seg))}
                    />
                  </div>
                  {(followUps.get(qn.id) ?? []).map((fu) => (
                    <FollowUpQuestion key={fu.id} question={fu} texts={texts} onText={onText} />
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
